import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@economic/gaudi';
import { refreshCurrentLegacyPage } from '@economic/react-app';

import { emailOptionsFromTemplates } from '../../invoices/utils/emailOptionsFromTemplates';
import { emailTemplateFormState } from '../../invoices/utils/emailTemplateFormState';
import { GetInformationQuoteResult } from '../../../../../../types/internal-api';
import { InvoiceLayoutType } from '../../../../../../types/internal-api-extra';
import { RouteDisclosure } from '../../../../../shared/components/RouteDisclosure';
import { SendDraftQuoteDialog } from './SendDraftQuoteDialog';
import { SendQuoteFormData, QuoteSendRequest } from '../types';
import { splitEmails } from '../../invoices/utils';
import { transformEmailOptions } from '../../invoices/utils/transformEmailOptions';
import { useEmailTemplates } from '../../invoices/utils/useEmailTemplates';
import { useLocalization, t } from '../../../../../shared/components/l10n/l10n';
import { usePostQuery } from '../../shared/hooks/usePostQuery';
import internalApiClient from '../../../../../shared/util/api/internalApiClient';

async function sendQuote(request: any): Promise<any> {
    return await internalApiClient.post(`/sales/quote/Send`, request);
}

function SendDraftQuoteDialogContainer() {
    const toast = useToast();
    const location = useLocation();
    const navigate = useNavigate();
    const { state = {} } = location;
    const { draftId: quoteId } = state as any;

    const { loading: loadingInformation, data, error: loadingInformationError } = usePostQuery<GetInformationQuoteResult>(
        `/sales/quote/GetInformation`,
        { quoteId },
        !quoteId
    );

    const loadingTexts = useLocalization(textIds);
    const { data: templates, loading: loadingTemplates } = useEmailTemplates(InvoiceLayoutType.Quote);

    const [sendingError, setSendingError] = useState<boolean>(false);
    const [sending, setSending] = useState<boolean>(false);

    const transformedInformation = useMemo(() => {
        if (!data) {
            return undefined;
        }

        const dataWithTranformedEmailOptions = transformEmailOptions(data);

        return {
            ...dataWithTranformedEmailOptions,
            emailOptions: {
                ...dataWithTranformedEmailOptions.emailOptions,
                ...emailOptionsFromTemplates(templates),
            },

            ...emailTemplateFormState(templates),
        };
    }, [data, templates]);

    async function send(values: SendQuoteFormData) {
        try {
            setSending(true);

            const requestData: QuoteSendRequest = {
                sendByType: values.sendByType,
                quoteId,
                showPdf: values.emailOptions.showPdf,
                emailOptions: {
                    subject: values.emailOptions.subject,
                    body: values.emailOptions.body,
                    encrypt: values.emailOptions.encrypt,
                    includePdfLink: values.emailOptions.includePdfLink,
                    includePdfAsAttachment: values.emailOptions.includePdfAsAttachment,
                    recipients: splitEmails(values.emailOptions.recipients),
                    recipientsCc: splitEmails(values.emailOptions.recipientsCc),
                    recipientsBcc: splitEmails(values.emailOptions.recipientsBcc),
                    saveSubject: values.emailOptions.saveSubject,
                    saveBody: values.emailOptions.saveBody,
                },
            };

            const result = await sendQuote(requestData);

            setSending(false);
            toast.success(t`TOAST_QUOTE_SEND_SUCCESS`);

            if (values.emailOptions.showPdf && result.data.pdfLink) {
                window.top && window.top.firebirdTask.then(() => window.top?.PopUp2(result.data.pdfLink, 500, 600));
            }

            refreshContentFrameAfterSuccess();
            hide();
        } catch (error) {
            if (error && error.response && error.response.status >= 500) {
                setSendingError(true);
            }
        }
    }

    function refreshContentFrameAfterSuccess() {
        refreshCurrentLegacyPage('content');
    }

    function hide() {
        navigate(location.pathname + location.search);
    }

    if (!quoteId) {
        return null;
    }

    return (
        <SendDraftQuoteDialog
            disclosure={<RouteDisclosure routeTrigger="#book-quote" />}
            loading={loadingInformation || loadingTexts || loadingTemplates || sending}
            information={transformedInformation}
            onSend={send}
            onHide={hide}
            error={sendingError || !!loadingInformationError}
        />
    );
}

const textIds = [
    'SHARED_FORM_OK',
    'FORM_BOOKANDSEND_QUOTE_ERRORS_HEADER_CANNOTSENDQUOTATION',
    'FORM_BOOKANDSEND_QUOTE_HEADER',
    'FORM_BOOKANDSEND_QUOTE_SINGLE_TEXT',
    'FORM_BOOKANDSEND_QUOTE_REGISTERONLY',
    'FORM_BOOKANDSEND_QUOTE_REGISTERANDSEND',
    'FORM_BOOKANDSEND_QUOTE_BOOK_ONLY_TEXT',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_OPEN',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_CLOSE',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_RECIPIENTS',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_TOGGLE_CC_BCC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_CC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_BCC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_SUBJECT',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_BODY',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_REMEMBER',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_OPEN',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_CLOSE',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_ATTACHMENT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_ENCRYPT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_LINK',
    'FORM_BOOKANDSEND_BOOK_TEXT',
    'FORM_BOOKANDSEND_SELECT_ATTACHMENT_ERROR',
    'FORM_BOOKANDSEND_SELECT_EMAIL_RECIPIENT_ERROR',
    'POPUP_INVOICEETC_ERROR_INVALIDEMAIL',
    'FORM_BOOKANDSEND_QUOTE_SHOWAFTERSENDING',
    'TOAST_QUOTE_SEND_SUCCESS',
    'FORM_BOOKANDSEND_ERRORS_UNKNOWN_ERROR',
];

export default SendDraftQuoteDialogContainer;
